import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { message } from 'antd';
import { ArrowLeft, Image, ChevronLeft, ChevronRight, User, Calendar } from 'lucide-react';
import { colors, spacing, radius, typography, transitions } from '../../../styles/theme';
import useResponsive from '../../../utils/useResponsive';
import brandDnaActions from '../../../actions/brandDna';

const PAGE_SIZE = 20;

export default function BrandGenerationsScreen() {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const { isMobile } = useResponsive();

  const [brand, setBrand] = useState(null);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [lightbox, setLightbox] = useState(null); // { url, alt }

  const fetchBrand = useCallback(async () => {
    try {
      const res = await brandDnaActions.getBrand(brandId);
      if (res.success) setBrand(res.data);
    } catch {
      navigate('/brand-dna');
    }
  }, [brandId, navigate]);

  const fetchGenerations = useCallback(async (targetPage) => {
    try {
      setLoading(true);
      const res = await brandDnaActions.getBrandGenerations(brandId, { page: targetPage, limit: PAGE_SIZE });
      if (res.success) {
        setItems(res.data.items || []);
        setPagination(res.data.pagination);
      }
    } catch (err) {
      message.error('Failed to load generations');
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => {
    fetchBrand();
  }, [fetchBrand]);

  useEffect(() => {
    fetchGenerations(page);
  }, [fetchGenerations, page]);

  const pad = isMobile ? spacing.md : spacing.xl;

  return (
    <div style={{ padding: pad, maxWidth: 1100, margin: '0 auto' }}>
      {/* Back + Header */}
      <motion.div
        whileHover={{ x: -2, color: colors.text.secondary }}
        onClick={() => navigate(`/brand-dna/${brandId}`)}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: colors.text.disabled, fontSize: 13, cursor: 'pointer', marginBottom: 32 }}
      >
        <ArrowLeft size={14} />
        Back to {brand?.brand_name || 'Brand DNA'}
      </motion.div>

      <div style={{ marginBottom: spacing.xl }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, marginBottom: 6 }}>
          <Image size={22} color={colors.brand.primary} />
          <h1 style={{ fontSize: typography.fontSize.xxl, fontWeight: typography.fontWeight.bold, color: colors.text.primary, margin: 0 }}>
            Generations
          </h1>
        </div>
        <p style={{ color: colors.text.tertiary, fontSize: typography.fontSize.md, margin: 0 }}>
          All images generated with this brand identity across all collaborators
        </p>
      </div>

      {/* Generation list */}
      {loading ? (
        <GenerationsSkeleton />
      ) : items.length === 0 ? (
        <EmptyGenerations />
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}>
            <AnimatePresence>
              {items.map((job, idx) => (
                <GenerationCard
                  key={job.image_job_id}
                  job={job}
                  index={idx}
                  onImageClick={(url) => setLightbox({ url, alt: extractPrompt(job) || 'Generated image' })}
                />
              ))}
            </AnimatePresence>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: spacing.md, marginTop: spacing.xxl }}>
              <PaginationBtn
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                icon={<ChevronLeft size={16} />}
              />
              <span style={{ color: colors.text.secondary, fontSize: typography.fontSize.sm }}>
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <PaginationBtn
                disabled={page >= pagination.totalPages}
                onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                icon={<ChevronRight size={16} />}
              />
            </div>
          )}
        </>
      )}

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            key="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
            style={{
              position: 'fixed', inset: 0, zIndex: 2000,
              background: 'rgba(0,0,0,0.88)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: spacing.xl,
            }}
          >
            <motion.img
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              src={lightbox.url}
              alt={lightbox.alt}
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: '90vw',
                maxHeight: '88vh',
                objectFit: 'contain',
                borderRadius: radius.lg,
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`@keyframes pulse { 0%,100%{opacity:1}50%{opacity:0.5} }`}</style>
    </div>
  );
}

function extractPrompt(job) {
  const t = job.input_texts;
  if (!t) return '';
  if (typeof t === 'string') return t;
  return t.positive_prompt || t.input_text || t.prompt || '';
}

function GenerationCard({ job, index, onImageClick }) {
  const [hovered, setHovered] = useState(false);

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return dateStr;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04, duration: 0.25 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        borderRadius: radius.lg,
        background: hovered ? colors.surface.secondary : colors.background.secondary,
        border: `1px solid ${hovered ? 'rgba(102,126,234,0.25)' : colors.border.secondary}`,
        padding: spacing.lg,
        transition: `background ${transitions.fast}, border-color ${transitions.fast}`,
      }}
    >
      {/* Meta row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md, marginBottom: spacing.md, flexWrap: 'wrap' }}>
        {job.created_by_name && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: colors.text.tertiary, fontSize: typography.fontSize.sm }}>
            <User size={12} />
            {job.created_by_name}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: colors.text.disabled, fontSize: typography.fontSize.sm }}>
          <Calendar size={12} />
          {formatDate(job.created_at)}
        </div>
        {job.results && (
          <div style={{ marginLeft: 'auto', color: colors.text.disabled, fontSize: typography.fontSize.xs }}>
            {job.results.length} image{job.results.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* Prompt */}
      {extractPrompt(job) && (
        <p style={{
          color: colors.text.secondary, fontSize: typography.fontSize.sm,
          margin: `0 0 ${spacing.md}px`,
          lineHeight: typography.lineHeight.relaxed,
          overflow: 'hidden', display: '-webkit-box',
          WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          fontStyle: 'italic',
        }}>
          &ldquo;{extractPrompt(job)}&rdquo;
        </p>
      )}

      {/* Images grid */}
      {job.results && job.results.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${Math.min(job.results.length, 4)}, 1fr)`,
          gap: spacing.sm,
        }}>
          {job.results.map((img) => (
            <motion.div
              key={img.image_id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onImageClick(img.external_url || img.content)}
              style={{ cursor: 'pointer', borderRadius: radius.md, overflow: 'hidden', aspectRatio: '1', background: colors.surface.primary }}
            >
              <img
                src={img.external_url || img.content}
                alt="Generated"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                loading="lazy"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </motion.div>
          ))}
        </div>
      )}
    </motion.div>
  );
}

function PaginationBtn({ onClick, disabled, icon }) {
  return (
    <motion.button
      whileHover={!disabled ? { background: colors.surface.secondary } : undefined}
      whileTap={!disabled ? { scale: 0.95 } : undefined}
      onClick={onClick}
      disabled={disabled}
      style={{
        width: 36, height: 36,
        borderRadius: radius.md,
        border: `1px solid ${colors.border.secondary}`,
        background: 'transparent',
        color: disabled ? colors.text.disabled : colors.text.secondary,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        transition: `background ${transitions.fast}`,
      }}
    >
      {icon}
    </motion.button>
  );
}

function GenerationsSkeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}>
      {[1, 2, 3].map((i) => (
        <div key={i} style={{ borderRadius: radius.lg, background: colors.background.secondary, border: `1px solid ${colors.border.secondary}`, padding: spacing.lg }}>
          <div style={{ height: 16, width: '30%', background: colors.surface.primary, borderRadius: 4, marginBottom: spacing.md, animation: 'pulse 1.5s ease-in-out infinite' }} />
          <div style={{ height: 14, width: '70%', background: colors.surface.primary, borderRadius: 4, marginBottom: spacing.md, animation: 'pulse 1.5s ease-in-out infinite' }} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: spacing.sm }}>
            {[1, 2, 3, 4].map((j) => (
              <div key={j} style={{ aspectRatio: '1', borderRadius: radius.md, background: colors.surface.primary, animation: 'pulse 1.5s ease-in-out infinite' }} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyGenerations() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{ textAlign: 'center', padding: `${spacing.xxxl * 2}px ${spacing.xl}px` }}
    >
      <div style={{
        width: 72, height: 72, borderRadius: radius.xl,
        background: `linear-gradient(135deg, rgba(102,126,234,0.1), rgba(118,75,162,0.1))`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px',
      }}>
        <Image size={32} color="#667eea" />
      </div>
      <h3 style={{ color: colors.text.primary, fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.semibold, margin: '0 0 8px' }}>
        No generations yet
      </h3>
      <p style={{ color: colors.text.tertiary, fontSize: typography.fontSize.md, margin: 0, maxWidth: 360, lineHeight: typography.lineHeight.relaxed }}>
        Start a conversation with the AI agent using this brand to generate images.
      </p>
    </motion.div>
  );
}
